import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './UserTypeSelection.css';

// Icons
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

const UserTypeSelection = ({ onUserTypeSelect, onBack }) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState(null);

  const userTypes = [
    {
      id: 'club_admin',
      name: t('clubAdmin'),
      description: t('clubAdminDescription'),
      icon: <AdminPanelSettingsIcon className="user-type-icon" />,
      color: '#e74c3c',
      selectedColor: '#0877BC'
    },
    {
      id: 'coach',
      name: t('coach'),
      description: t('coachDescription'),
      icon: <SchoolIcon className="user-type-icon" />,
      color: '#f39c12',
      selectedColor: '#0877BC'
    },
    {
      id: 'player',
      name: t('player'),
      description: t('playerDescription'),
      icon: <PersonIcon className="user-type-icon" />,
      color: '#27ae60',
      selectedColor: '#0877BC'
    },
    {
      id: 'parent',
      name: t('parentRoleName', 'Parent / Guardian'),
      description: t('parentRoleDescription', "Register and follow your children's table tennis journey"),
      icon: <FamilyRestroomIcon className="user-type-icon" />,
      color: '#ffa451',
      selectedColor: '#0877BC'
    }
  ];

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleContinue = () => {
    if (selectedType) {
      onUserTypeSelect(selectedType);
    }
  };

  // Handle Enter key press
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' && selectedType) {
        handleContinue();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedType]);

  return (
    <div className="user-type-selection-container">
      <div className="user-type-card">
        <div className="user-type-header">
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
          <h1 className="user-type-title">{t('selectUserType')}</h1>
          <p className="user-type-subtitle">{t('selectUserTypeDescription')}</p>
        </div>

        <div className="user-type-options">
          {userTypes.map((type, index) => (
            <div
              key={type.id}
              className={`user-type-option ${selectedType === type.id ? 'selected' : ''}`}
              onClick={() => handleTypeSelect(type.id)}
              style={{ 
                '--type-color': selectedType === type.id ? type.selectedColor : type.color,
                '--animation-delay': `${index * 0.1}s`
              }}
            >
              <div className="user-type-icon-container">
                {type.icon}
                <div className="icon-glow"></div>
              </div>
              <div className="user-type-content">
                <h3 className="user-type-name">{type.name}</h3>
                <p className="user-type-description">{type.description}</p>
                <div className="user-type-features">
                  {type.id === 'club_admin' && (
                    <span className="feature-tag">Management</span>
                  )}
                  {type.id === 'coach' && (
                    <span className="feature-tag">Training</span>
                  )}
                  {type.id === 'player' && (
                    <span className="feature-tag">Competition</span>
                  )}
                </div>
              </div>
              <div className="user-type-check">
                {selectedType === type.id && (
                  <div className="checkmark">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="user-type-actions">
          {onBack && (
            <button 
              type="button" 
              className="back-button"
              onClick={onBack}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('back')}
            </button>
          )}
          <div className="continue-section">
            <button
              type="button"
              className={`continue-button ${selectedType ? 'enabled' : 'disabled'}`}
              onClick={handleContinue}
              disabled={!selectedType}
              title={selectedType ? `${t('continue')} (Enter)` : t('continue')}
            >
              {t('continue')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {selectedType && (
              <p className="keyboard-hint">Press Enter to continue</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTypeSelection;
